import axios from "axios";
import type { AxiosInstance } from "axios";

export interface ChecklistDefinition {
  id: string;
  level: number;
  weight: number;
  country: string;
  levelName: string | null;
}

export interface ChecklistStep {
  id: string;
  status: string;
  definition: ChecklistDefinition;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistResponse {
  success: boolean;
  data: {
    percentage: number;
    totalDefinitions: number;
    completedDefinitions: number;
    steps: ChecklistStep[];
  };
  requestId?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

// Configuração do axios com CORS e credentials
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Interceptor de requisição para garantir que os cookies sejam enviados
axiosInstance.interceptors.request.use(
  (config) => {
    config.withCredentials = true;

    if (typeof document !== "undefined" && import.meta.env.DEV) {
      const cookies = document.cookie;
      console.log("[checklistApi] Cookies disponíveis:", cookies);
      console.log("[checklistApi] URL da requisição:", config.url);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor de resposta para debug
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error("[checklistApi] Erro na resposta:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      });
    }
    return Promise.reject(error);
  },
);

export async function getChecklist(): Promise<ChecklistResponse> {
  try {
    const response = await axiosInstance.get<ChecklistResponse>("/checklist");
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;
      throw new Error(
        `Erro ao buscar checklist: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error(
        "Erro de rede ao buscar checklist. Verifique sua conexão.",
      );
    } else {
      throw new Error(`Erro ao buscar checklist: ${error.message}`);
    }
  }
}
